export const PUBLIC_LEAGUE_HUB_URL = "https://winterborne1.com/teambeer/leaguehub/" as const;

export interface LeagueHubBrowserRouteConfig {
  publicLeagueHubUrl: string;
}

export interface BrowserLocationIdentity {
  protocol: string;
  hostname: string;
  port: string;
  pathname: string;
}

export interface EmailVerificationLocation {
  pathname: string;
  search: string;
  hash: string;
}

export type EmailVerificationLink =
  | { kind: "none" }
  | { kind: "missing" }
  | { kind: "invalid" }
  | { kind: "token"; token: string; source: "fragment" | "query" };

export type EmailVerificationControllerKind = "none" | "missing" | "invalid" | "pending";

export interface EmailVerificationController {
  readonly kind: EmailVerificationControllerKind;
  confirm: () => Promise<void>;
  clear: () => void;
}

const DEFAULT_CONFIG: LeagueHubBrowserRouteConfig = {
  publicLeagueHubUrl: PUBLIC_LEAGUE_HUB_URL,
};

const OPAQUE_VERIFICATION_TOKEN = /^[A-Za-z0-9_-]{43}$/u;

function verificationRoute(pathname: string): boolean {
  const normalized = pathname.toLocaleLowerCase("en-US").replace(/\/$/u, "");
  return normalized === "/verify" || normalized === "/verify-email";
}

/**
 * Reads a public email-verification link without broad substring routing.
 * Fragment tokens are preferred for new links because fragments never reach
 * the HTTP server; the query form remains as a legacy fallback. A link with
 * duplicate or competing token values is rejected instead of guessing which
 * bearer credential the user intended to submit.
 */
export function emailVerificationLinkFromLocation(
  location: EmailVerificationLocation,
): EmailVerificationLink {
  if (!verificationRoute(location.pathname)) return { kind: "none" };

  const fragment = new URLSearchParams(location.hash.replace(/^#\??/u, ""));
  const query = new URLSearchParams(location.search);
  const fragmentTokens = fragment.getAll("token");
  const queryTokens = query.getAll("token");

  if (fragmentTokens.length > 0) {
    if (fragmentTokens.length !== 1 || queryTokens.length > 0) return { kind: "invalid" };
    const token = fragmentTokens[0] ?? "";
    return OPAQUE_VERIFICATION_TOKEN.test(token)
      ? { kind: "token", token, source: "fragment" }
      : { kind: "invalid" };
  }

  if (queryTokens.length === 0) return { kind: "missing" };
  if (queryTokens.length !== 1) return { kind: "invalid" };
  const token = queryTokens[0] ?? "";
  return OPAQUE_VERIFICATION_TOKEN.test(token)
    ? { kind: "token", token, source: "query" }
    : { kind: "invalid" };
}

/**
 * Returns the browser-history entry for this deployed Hub. The public file
 * path is used only on its exact configured origin; local/LAN QA stays at `/`.
 * API and WebSocket routes deliberately remain origin-rooted elsewhere.
 */
export function leagueHubBrowserEntryPath(
  location: BrowserLocationIdentity,
  config: LeagueHubBrowserRouteConfig = DEFAULT_CONFIG,
): string {
  try {
    const target = new URL(config.publicLeagueHubUrl);
    const runtimeOrigin = new URL(
      `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ""}`,
    ).origin;
    if (
      target.username
      || target.password
      || !["http:", "https:"].includes(target.protocol)
      || !target.pathname.startsWith("/")
      || target.pathname === "/"
    ) {
      return "/";
    }
    // The main-site local facade hosts the Hub at the same canonical path on
    // a LAN origin. Keep that facade route after verification/logout so its
    // wrapper does not interpret `/` as the Draft Lab. Standalone Hub QA at
    // the local root continues to use `/`.
    return target.origin === runtimeOrigin || location.pathname === target.pathname
      ? target.pathname
      : "/";
  } catch {
    return "/";
  }
}

/**
 * Captures and scrubs a verification bearer credential synchronously. Call
 * this during module evaluation, before React or any application request can
 * run. The injected history adapter makes scrubbing deterministic in unit
 * tests. The token remains only inside the returned controller closure until
 * confirmation succeeds or the caller clears a terminal result.
 */
export function captureEmailVerificationController(
  location: BrowserLocationIdentity & EmailVerificationLocation,
  replaceState: (data: unknown, unused: string, url?: string | URL | null) => void,
  submit: (token: string) => Promise<void>,
  config: LeagueHubBrowserRouteConfig = DEFAULT_CONFIG,
): EmailVerificationController {
  const link = emailVerificationLinkFromLocation(location);
  if (link.kind !== "none") {
    replaceState({}, "", leagueHubBrowserEntryPath(location, config));
  }

  // Keep the bearer entirely inside this closure. Consumers can observe only
  // a sanitized route kind and invoke an attempt; React never receives the
  // credential itself. A successful attempt destroys the token, while a
  // rejected attempt retains it only until the caller classifies the failure.
  let pendingToken = link.kind === "token" ? link.token : undefined;
  let inFlight: Promise<void> | undefined;
  const kind: EmailVerificationControllerKind = link.kind === "token" ? "pending" : link.kind;

  return {
    kind,
    confirm() {
      if (!pendingToken) return Promise.reject(new Error("No email verification token is available."));
      if (inFlight) return inFlight;
      inFlight = submit(pendingToken)
        .then(() => {
          pendingToken = undefined;
        })
        .finally(() => {
          inFlight = undefined;
        });
      return inFlight;
    },
    clear() {
      pendingToken = undefined;
    },
  };
}
